import React, { useState, useContext, useEffect } from "react";
import companyBasedRequest from "../../../request/companyBasedRequest";
import translations from "../../../translations/translations";
import "../adminModals.css";
import "./shopHoursModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import useModalDismiss from "../../common/useModalDismiss";

const DEFAULT_HOURS = [
    { dayEN: "Monday", dayES: "Lunes", open: "09:00", close: "17:00", closed: false },
    { dayEN: "Tuesday", dayES: "Martes", open: "09:00", close: "17:00", closed: false },
    { dayEN: "Wednesday", dayES: "Miércoles", open: "09:00", close: "17:00", closed: false },
    { dayEN: "Thursday", dayES: "Jueves", open: "09:00", close: "17:00", closed: false },
    { dayEN: "Friday", dayES: "Viernes", open: "09:00", close: "17:00", closed: false },
    { dayEN: "Saturday", dayES: "Sábado", open: "09:00", close: "17:00", closed: false },
    { dayEN: "Sunday", dayES: "Domingo", open: "09:00", close: "17:00", closed: false },
];

const ShopHoursModal = ({ open, onClose, shopData, setShopData }) => {
    useModalDismiss(onClose, { enabled: open });
    const { language, setUserData, userData } = useContext(CombinedContext);

    const [localHours, setLocalHours] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (shopData?.openingHours?.length > 0) {
            setLocalHours([...shopData.openingHours]);
        } else {
            setLocalHours([...DEFAULT_HOURS]);
        }
    }, [shopData]);

    if (!open) return null;

    const handleHourChange = (index, field, value) => {
        setLocalHours((prev) => {
            const updated = [...prev];
            const day = { ...updated[index] };

            // helper: "HH:MM" -> minutes
            const toMinutes = (time) => {
                if (!time) return null;
                const [h, m] = time.split(":").map(Number);
                return h * 60 + m;
            };

            // proposed new values
            const newOpen = field === "open" ? value : day.open;
            const newClose = field === "close" ? value : day.close;

            // if day is open and both times present, enforce open < close
            if (!day.closed && newOpen && newClose) {
                const openMins = toMinutes(newOpen);
                const closeMins = toMinutes(newClose);

                if (openMins >= closeMins) {
                    // ❌ reject invalid change, keep previous state
                    return prev;
                }
            }

            // ✅ accept change
            day[field] = value;
            updated[index] = day;
            return updated;
        });
    };

    const times = Array.from({ length: 48 }, (_, i) => {
        const h = Math.floor(i / 2);
        const m = i % 2 === 0 ? "00" : "30";
        return `${String(h).padStart(2, "0")}:${m}`;
    });

    const toggleClosed = (index) => {
        setLocalHours((prev) => {
            const updated = [...prev];
            const day = { ...updated[index] };

            day.closed = !day.closed;

            // ✅ When opening a day, set safe defaults
            if (!day.closed) {
                day.open = "09:00";
                day.close = "17:00";
            } else {
                // 👇 When closing, clear hours
                day.open = "00:00";
                day.close = "00:00";
            }

            updated[index] = day;
            return updated;
        });
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const res = await companyBasedRequest.updateShopHours(
                shopData.shopId,
                { openingHours: localHours },
                userData.cognitoTokens.AccessToken
            );

            setShopData((prev) => {
                const hasSteps = prev?.setup && prev?.setup.steps && typeof prev.setup.steps === "object";

                return {
                    ...prev,
                    openingHours: localHours,

                    // ✅ only update steps if they exist
                    ...(hasSteps && {
                        setup: {
                            ...prev.setup,
                            steps: {
                                ...prev.setup.steps,
                                hours: true,
                            },
                        },
                    }),
                };
            });

            // ✅ backend is source of truth
            setUserData((prev) => ({
                ...prev,
                shop: res.shop,
            }));

            onClose();
        } catch (err) {
            console.error("Failed to update hours:", err);
        } finally {
            setSaving(false);
        }
    };

	function formatTimeDisplay(time) {
		if (!time) return "";

		const [h, m] = time.split(":").map(Number);
		const hour12 = h % 12 || 12;
		const minutes = String(m).padStart(2, "0");
		const period = h >= 12 ? "PM" : "AM";

		return `${hour12}:${minutes} ${period}`;
	}

    const t = translations[language].profile;
    const hoursT = t.modal.hours;
    const openDays = localHours.filter((d) => !d.closed).length;

    return (
        <div className="sf-pm-overlay">
            <div className="sf-pm-modal">
                {/* HEADER */}
                <div className="sf-pm-header">
                    <div className="sf-pm-heading">
                        <h2 className="sf-pm-title">{t.sidebar.hours.title}</h2>
                        <p className="sf-pm-sub">{hoursT.description}</p>
                    </div>
                    <button className="sf-pm-close" onClick={onClose} aria-label={t.actions.cancel}>
                        ✕
                    </button>
                </div>

                {/* BODY */}
                <div className="sf-pm-body">
                    <section className="sf-pm-section">
                        <div className="sf-pm-label-row">
                            <p className="sf-pm-label">{t.sidebar.hours.title}</p>
                            <span className="sf-pm-count">
                                {openDays}/{localHours.length} {hoursT.openCount}
                            </span>
                        </div>

                        <div className="sf-pm-card sf-pm-days">
                            {localHours.map((day, index) => {
                                const label = language === "es" ? day.dayES : day.dayEN;

                                return (
                                    <div className={`sf-pm-day${day.closed ? " is-closed" : ""}`} key={index}>
                                        <div className="sf-pm-day-head">
                                            <span className="sf-pm-day-name">{label}</span>

                                            {day.closed && (
                                                <span className="sf-pm-day-status">{t.sidebar.hours.closed}</span>
                                            )}

                                            <input
                                                type="checkbox"
                                                className="sf-pm-switch"
                                                checked={!day.closed}
                                                onChange={() => toggleClosed(index)}
                                                aria-label={`${label} — ${
                                                    day.closed ? hoursT.markOpen : hoursT.markClosed
                                                }`}
                                            />
                                        </div>

                                        {!day.closed && (
                                            <div className="sf-pm-grid-2 sf-pm-day-times">
                                                <div className="sf-pm-field">
                                                    <label className="sf-pm-field-label">{hoursT.open}</label>
                                                    <select
                                                        className="sf-pm-select"
                                                        value={day.open}
                                                        onChange={(e) => handleHourChange(index, "open", e.target.value)}
                                                    >
                                                        {times.map((time) => (
                                                            <option key={time} value={time}>
                                                                {formatTimeDisplay(time)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="sf-pm-field">
                                                    <label className="sf-pm-field-label">{hoursT.close}</label>
                                                    <select
                                                        className="sf-pm-select"
                                                        value={day.close}
                                                        onChange={(e) =>
                                                            handleHourChange(index, "close", e.target.value)
                                                        }
                                                    >
                                                        {times
                                                            .filter((time) => time > day.open)
                                                            .map((time) => (
                                                                <option key={time} value={time}>
                                                                    {formatTimeDisplay(time)}
                                                                </option>
                                                            ))}
                                                    </select>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                </div>

                {/* FOOTER */}
                <div className="sf-pm-footer">
                    <button className="sf-pm-btn is-ghost" onClick={onClose} disabled={saving}>
                        {translations[language].calendar.editor.cancel}
                    </button>
                    <button className="sf-pm-btn is-primary" onClick={handleSave} disabled={saving}>
                        {saving && <span className="sf-pm-spinner" aria-hidden="true" />}
                        {saving
                            ? translations[language].calendar.editor.saving
                            : translations[language].calendar.editor.save}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ShopHoursModal;
